import { useEffect, useState } from "react";
import type { BookDetail, BookListItem } from "@atlas/contracts";
import {
  addBookNote,
  addReadingLogEntry,
  ApiError,
  deleteBook,
  fetchBook,
  fetchBooks,
  updateBook,
} from "../../lib/api";
import { statusLabel } from "../../lib/statusLabels";
import { statusTone } from "../../lib/statusTone";
import { formatNumberFa } from "../../lib/format";
import { AppLink } from "../AppLink";
import { AddBookSheet } from "../AddBookSheet";
import { DomainListPage } from "../DomainListPage";
import { DomainDetailPage } from "../DomainDetailPage";
import { DetailActionMenu } from "../DetailActionMenu";
import { RatingBadge, RatingDot } from "../RatingBadge";
import { EmptyState } from "../StateViews";
import { Badge } from "../ui/Badge";
import { Progress } from "../ui/Progress";
import { Sheet } from "../ui/Sheet";
import { AlertDialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { Field, Input, Textarea } from "../ui/Input";
import { ChipGroup } from "../ui/Select";
import { useToast } from "../ui/Toast";
import { Icon } from "../Icon";

const PAGE_SIZE = 20;
const BOOK_STATUSES: Array<{ value: "" | "unread" | "reading" | "read"; label: string }> = [
  { value: "", label: "همه وضعیت‌ها" },
  { value: "unread", label: "نخوانده" },
  { value: "reading", label: "در حال خواندن" },
  { value: "read", label: "خوانده‌شده" },
];

interface BooksListPageProps {
  onNavigate: (path: string) => void;
  onOpenSearch: () => void;
  /** Set by the Quick Add flow to jump straight into the add-book sheet. */
  autoOpenAdd?: boolean;
  onAutoOpenHandled?: () => void;
}

export function BooksListPage({ onNavigate, onOpenSearch, autoOpenAdd, onAutoOpenHandled }: BooksListPageProps) {
  const [status, setStatus] = useState<"" | "unread" | "reading" | "read">("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<BookListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchBooks(
      { status: status || undefined, q: query.trim() || undefined, limit: PAGE_SIZE, offset: 0 },
      controller.signal,
    )
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setItems(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری کتاب‌ها با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, query, reloadToken]);

  useEffect(() => {
    if (!autoOpenAdd) return;
    setAddOpen(true);
    onAutoOpenHandled?.();
    // Only re-run when the Quick Add flow flips the trigger on; reading the
    // latest onAutoOpenHandled via closure is fine since this effect always
    // consumes and clears the trigger immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd]);

  function handleBookCreated(book: BookDetail) {
    setAddOpen(false);
    setReloadToken((t) => t + 1);
    onNavigate(`/books/${book.slug}`);
  }

  const statusLabelText = BOOK_STATUSES.find((s) => s.value === status)?.label ?? "همه وضعیت‌ها";
  const filterSummary = items ? `${statusLabelText} · نمایش ${formatNumberFa(items.length)} از ${formatNumberFa(total)}` : statusLabelText;

  return (
    <>
      <DomainListPage
        title="کتاب‌ها"
        onOpenSearch={onOpenSearch}
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="جست‌وجو در کتاب‌ها"
        onOpenFilterSheet={() => setFilterOpen(true)}
        filterActive={status !== ""}
        filterSummary={filterSummary}
        items={items}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        emptyTitle="کتابی با این فیلتر پیدا نشد"
        emptyHint="فیلتر را تغییر بده یا پاک کن"
        getKey={(item) => item.id}
        renderItem={(item) => (
          <AppLink href={`/books/${item.slug}`} onNavigate={onNavigate} className="item-row">
            {item.assetUrl ? (
              <img src={item.assetUrl} alt={item.titleFa} className="item-row__media item-row__media--book" />
            ) : (
              <span className="item-row__media item-row__media--book">جلد کتاب</span>
            )}
            <span className="item-row__body">
              <span className="item-row__top">
                <span className="item-row__title">{item.titleFa}</span>
                <Badge tone={statusTone(item.status)}>{statusLabel(item.status)}</Badge>
              </span>
              {item.titleEn && <span className="item-row__subtitle">{item.titleEn}</span>}
              <span className="item-row__meta">
                <RatingDot rating={item.rating} />
                {item.publisher && (
                  <>
                    <span>·</span>
                    <span>
                      {item.publisher}
                      {item.year ? `، ${item.year}` : ""}
                    </span>
                  </>
                )}
              </span>
            </span>
          </AppLink>
        )}
      />
      <Sheet open={filterOpen} onClose={() => setFilterOpen(false)} title="فیلتر و مرتب‌سازی کتاب‌ها">
        <div className="filter-group">
          <p className="filter-group__label">وضعیت</p>
          <ChipGroup
            options={BOOK_STATUSES.map((s) => ({ label: s.label, value: s.value }))}
            value={status}
            onChange={setStatus}
            ariaLabel="وضعیت"
          />
        </div>
        <div className="sheet-actions">
          <Button variant="secondary" onClick={() => setStatus("")}>
            پاک‌کردن فیلترها
          </Button>
          <Button variant="primary" onClick={() => setFilterOpen(false)}>
            اعمال فیلتر
          </Button>
        </div>
      </Sheet>
      <AddBookSheet open={addOpen} onClose={() => setAddOpen(false)} onCreated={handleBookCreated} />
    </>
  );
}

interface BookDetailPageProps {
  slug: string;
  onNavigate: (path: string) => void;
}

type ActiveSheet = "edit" | "status" | "note" | "page" | "log" | null;

export function BookDetailPage({ slug, onNavigate }: BookDetailPageProps) {
  const [book, setBook] = useState<BookDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const [formTitleFa, setFormTitleFa] = useState("");
  const [formTitleEn, setFormTitleEn] = useState("");
  const [formPublisher, setFormPublisher] = useState("");
  const [formYear, setFormYear] = useState("");
  const [formStatus, setFormStatus] = useState<"unread" | "reading" | "read">("unread");
  const [formNote, setFormNote] = useState("");
  const [formPage, setFormPage] = useState("");
  const [formLogPage, setFormLogPage] = useState("");
  const [formLogNote, setFormLogNote] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchBook(slug, controller.signal)
      .then(setBook)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setBook(null);
        setError(err instanceof ApiError ? err.message : "بارگذاری کتاب با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, reloadToken]);

  function openEdit() {
    if (!book) return;
    setFormTitleFa(book.titleFa);
    setFormTitleEn(book.titleEn ?? "");
    setFormPublisher(book.publisher ?? "");
    setFormYear(book.year ?? "");
    setActiveSheet("edit");
  }

  function openStatus() {
    if (!book) return;
    setFormStatus(book.status);
    setActiveSheet("status");
  }

  function openPage() {
    if (!book) return;
    setFormPage(String(book.currentPage ?? 0));
    setActiveSheet("page");
  }

  async function submitEdit() {
    if (!book) return;
    setBusy(true);
    try {
      const updated = await updateBook(book.slug, {
        titleFa: formTitleFa.trim() || undefined,
        titleEn: formTitleEn,
        publisher: formPublisher,
        year: formYear,
      });
      setBook(updated);
      setActiveSheet(null);
      toast.push("تغییرات ذخیره شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ذخیره تغییرات با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitStatus() {
    if (!book) return;
    setBusy(true);
    try {
      const updated = await updateBook(book.slug, { status: formStatus });
      setBook(updated);
      setActiveSheet(null);
      toast.push("وضعیت به‌روزرسانی شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "تغییر وضعیت با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitNote() {
    if (!book || !formNote.trim()) return;
    setBusy(true);
    try {
      const note = await addBookNote(book.slug, { body: formNote.trim() });
      setBook((prev) => (prev ? { ...prev, bookNotes: [note, ...prev.bookNotes] } : prev));
      setFormNote("");
      setActiveSheet(null);
      toast.push("یادداشت افزوده شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "افزودن یادداشت با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitPage() {
    if (!book) return;
    const page = Number(formPage);
    if (!Number.isInteger(page) || page < 0) return;
    setBusy(true);
    try {
      const updated = await updateBook(book.slug, { currentPage: page });
      setBook(updated);
      setActiveSheet(null);
      toast.push("صفحه فعلی ثبت شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت صفحه با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function submitLog() {
    if (!book || !formLogNote.trim()) return;
    setBusy(true);
    try {
      const page = formLogPage.trim() ? Number(formLogPage) : undefined;
      const entry = await addReadingLogEntry(book.slug, { page, note: formLogNote.trim() });
      setBook((prev) => (prev ? { ...prev, readingLog: [entry, ...prev.readingLog] } : prev));
      setFormLogPage("");
      setFormLogNote("");
      setActiveSheet(null);
      toast.push("گزارش مطالعه افزوده شد.");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "افزودن گزارش با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  async function confirmAndDelete() {
    if (!book) return;
    setBusy(true);
    try {
      await deleteBook(book.slug);
      toast.push("کتاب حذف شد.");
      onNavigate("/books");
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "حذف با خطا مواجه شد.", "danger");
      setBusy(false);
      setConfirmDelete(false);
    }
  }

  const hasProgress = typeof book?.totalPages === "number" && book.totalPages > 0;
  const progressPct = hasProgress ? Math.min(100, ((book!.currentPage ?? 0) / book!.totalPages!) * 100) : 0;

  return (
    <>
      <DomainDetailPage
        backHref="/books"
        backLabel="بازگشت به کتاب‌ها"
        onNavigate={onNavigate}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((t) => t + 1)}
        hero={book?.assetUrl ? (
          <img src={book.assetUrl} alt={book?.titleFa} className="detail-hero__cover detail-hero__cover--book" />
        ) : (
          <span className="detail-hero__cover detail-hero__cover--book">جلد کتاب</span>
        )}
      >
        {book && (
          <div className="detail-page__body" style={{ padding: "0 1.1rem" }}>
            <h1 className="detail-title">{book.titleFa}</h1>
            {book.titleEn && <p className="detail-subtitle">{book.titleEn}</p>}

            <div className="detail-badges">
              <Badge tone={statusTone(book.status)}>{statusLabel(book.status)}</Badge>
              <RatingBadge rating={book.rating} />
            </div>

            <div className="info-card">
              <div className="meta-grid">
                {book.publisher && (
                  <div>
                    <p className="meta-item__label">
                      <Icon name="tag" width={13} height={13} aria-hidden="true" />
                      ناشر
                    </p>
                    <p className="meta-item__value">{book.publisher}</p>
                  </div>
                )}
                {book.year && (
                  <div>
                    <p className="meta-item__label">
                      <Icon name="calendar" width={13} height={13} aria-hidden="true" />
                      سال
                    </p>
                    <p className="meta-item__value">{book.year}</p>
                  </div>
                )}
                {book.location && (
                  <div>
                    <p className="meta-item__label">محل نگهداری</p>
                    <p className="meta-item__value">{book.location}</p>
                  </div>
                )}
              </div>
              {hasProgress && (
                <>
                  <p className="meta-item__label">
                    <Icon name="book" width={13} height={13} aria-hidden="true" />
                    پیشرفت
                  </p>
                  <p className="meta-item__value" style={{ marginBottom: 8 }}>
                    صفحه {formatNumberFa(book.currentPage ?? 0)} از {formatNumberFa(book.totalPages ?? 0)}
                  </p>
                  <Progress value={progressPct} />
                </>
              )}
              <div className="info-card__actions">
                <button type="button" className="info-card__action" onClick={openPage}>
                  ثبت صفحهٔ فعلی
                </button>
                <button type="button" className="info-card__action" onClick={() => setActiveSheet("log")}>
                  افزودن گزارش مطالعه
                </button>
              </div>
            </div>

            {book.genres.length > 0 && (
              <div className="section">
                <p className="section__label">
                  <Icon name="tag" width={13} height={13} aria-hidden="true" />
                  ژانر
                </p>
                <div className="chip-list">
                  {book.genres.map((g) => (
                    <span key={g} className="chip">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {book.contributors.length > 0 && (
              <div className="section">
                <p className="section__label">
                  <Icon name="users" width={13} height={13} aria-hidden="true" />
                  افراد
                </p>
                <div className="people-list">
                  {book.contributors.map((c, i) => (
                    <div key={`${c.role}-${c.name}-${i}`} className="person-row">
                      <span className="person-row__avatar" aria-hidden="true">
                        چهره
                      </span>
                      <div>
                        <p className="person-row__name">{c.name}</p>
                        <p className="person-row__role">{c.role === "author" ? "نویسنده" : "مترجم"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {book.notes && (
              <div className="section">
                <p className="section__label">
                  <Icon name="notebook" width={13} height={13} aria-hidden="true" />
                  یادداشت
                </p>
                <p className="notes-box">{book.notes}</p>
              </div>
            )}

            <div className="section">
              <p className="section__label">
                <Icon name="notebook" width={13} height={13} aria-hidden="true" />
                یادداشت‌ها
              </p>
              {book.bookNotes.length === 0 && <p className="notes-box notes-box--muted">یادداشتی ثبت نشده.</p>}
              {book.bookNotes.length > 0 && (
                <div className="log-list">
                  {book.bookNotes.map((n) => (
                    <p key={n.id} className="notes-box">
                      {n.body}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <div className="section">
              <p className="section__label">
                <Icon name="clock" width={13} height={13} aria-hidden="true" />
                گزارش خواندن
              </p>
              {book.readingLog.length === 0 && <EmptyState message="هنوز گزارشی ثبت نشده." />}
              {book.readingLog.length > 0 && (
                <div className="log-list">
                  {book.readingLog.map((entry) => (
                    <div key={entry.id}>
                      <p className="log-entry__meta">
                        {typeof entry.page === "number" ? `صفحه ${formatNumberFa(entry.page)}` : ""}
                      </p>
                      <p className="log-entry__text">{entry.note}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DomainDetailPage>

      {book && (
        <DetailActionMenu
          actions={[
            { label: "ویرایش", icon: "notebook", onClick: openEdit },
            { label: "تغییر وضعیت", icon: "check", onClick: openStatus },
            { label: "افزودن یادداشت", icon: "notebook", onClick: () => setActiveSheet("note") },
            { label: "حذف", icon: "x", onClick: () => setConfirmDelete(true), danger: true },
          ]}
        />
      )}

      <Sheet open={activeSheet === "edit"} onClose={() => setActiveSheet(null)} title="ویرایش کتاب">
        <Field label="عنوان">
          <Input value={formTitleFa} onChange={(e) => setFormTitleFa(e.target.value)} />
        </Field>
        <Field label="عنوان انگلیسی">
          <Input value={formTitleEn} onChange={(e) => setFormTitleEn(e.target.value)} />
        </Field>
        <Field label="ناشر">
          <Input value={formPublisher} onChange={(e) => setFormPublisher(e.target.value)} />
        </Field>
        <Field label="سال">
          <Input value={formYear} onChange={(e) => setFormYear(e.target.value)} dir="ltr" />
        </Field>
        <Button variant="primary" block onClick={submitEdit} disabled={busy} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "status"} onClose={() => setActiveSheet(null)} title="تغییر وضعیت">
        <ChipGroup
          options={[
            { label: "نخوانده", value: "unread" },
            { label: "در حال خواندن", value: "reading" },
            { label: "خوانده‌شده", value: "read" },
          ]}
          value={formStatus}
          onChange={(v) => setFormStatus(v as typeof formStatus)}
          ariaLabel="وضعیت"
        />
        <Button variant="primary" block onClick={submitStatus} disabled={busy} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "note"} onClose={() => setActiveSheet(null)} title="افزودن یادداشت">
        <Field label="یادداشت">
          <Textarea rows={4} value={formNote} onChange={(e) => setFormNote(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitNote} disabled={busy || !formNote.trim()} style={{ marginTop: 14 }}>
          افزودن
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "page"} onClose={() => setActiveSheet(null)} title="ثبت صفحهٔ فعلی">
        <Field label="صفحه فعلی">
          <Input type="number" dir="ltr" value={formPage} onChange={(e) => setFormPage(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitPage} disabled={busy} style={{ marginTop: 14 }}>
          ثبت
        </Button>
      </Sheet>

      <Sheet open={activeSheet === "log"} onClose={() => setActiveSheet(null)} title="افزودن گزارش مطالعه">
        <Field label="صفحه (اختیاری)">
          <Input type="number" dir="ltr" value={formLogPage} onChange={(e) => setFormLogPage(e.target.value)} />
        </Field>
        <Field label="یادداشت">
          <Textarea rows={4} value={formLogNote} onChange={(e) => setFormLogNote(e.target.value)} />
        </Field>
        <Button
          variant="primary"
          block
          onClick={submitLog}
          disabled={busy || !formLogNote.trim()}
          style={{ marginTop: 14 }}
        >
          افزودن
        </Button>
      </Sheet>

      <AlertDialog
        open={confirmDelete}
        title="حذف کتاب"
        message={`آیا از حذف «${book?.titleFa ?? ""}» مطمئن هستید؟ این کار قابل بازگشت نیست.`}
        confirmLabel="حذف"
        destructive
        busy={busy}
        onConfirm={confirmAndDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
